import React, { useMemo } from 'react'

import { IRecommendation } from '@/interfaces/recommendation'
import { search as searchService } from '@/services/search'
import { useRouter } from 'next/router'
import { SearchContextV2 } from './SearchContext'
import { searchReducer } from './searchReducer'
import { INITIAL_SEARCH_STATE, SearchActionTypes } from './types'

interface Props {
  children: React.ReactNode
}

const SEARCH_HISTORY_LOCAL_STORAGE_KEY = 'search-history'
export const SEARCH_RESULTS_PAGE_SIZE = 50

export const SearchProviderV2: React.FC<Props> = ({ children }) => {
  const { pathname } = useRouter()
  const [state, dispatch] = React.useReducer(
    searchReducer,
    INITIAL_SEARCH_STATE
  )
  const { searchHistory, searchResults, searchTerm } = state
  const [abortController, setAbortController] =
    React.useState<AbortController>(null)
  const [timeoutSearch, setTimeoutSearch] = React.useState<NodeJS.Timeout>(null)

  React.useEffect(() => {
    const searchHistoryFromLocalStorage = localStorage.getItem(
      SEARCH_HISTORY_LOCAL_STORAGE_KEY
    )
    if (searchHistoryFromLocalStorage) {
      dispatch({
        type: SearchActionTypes.RECOVER_SEARCH_HISTORY,
        payload: JSON.parse(searchHistoryFromLocalStorage),
      })
    }
  }, [])

  React.useEffect(() => {
    if (!searchHistory?.length) return
    localStorage.setItem(
      SEARCH_HISTORY_LOCAL_STORAGE_KEY,
      JSON.stringify(searchHistory)
    )
  }, [searchHistory])

  const isOnSearchPage = useMemo(() => {
    return pathname.startsWith('/search')
  }, [pathname])

  const search = async (searchTerm?: string) => {
    setLoading(true)
    setSearchBoxActive(true)
    dispatch({
      type: SearchActionTypes.SET_NO_MORE_RESULTS,
      payload: false,
    })
    const newAbortController = new AbortController()
    setAbortController(newAbortController)
    abortController?.abort()
    
    const res = await searchService(
      {
        searchTerm: searchTerm,
        skipCount: 0,
        pageSize: SEARCH_RESULTS_PAGE_SIZE,
      },
      newAbortController
    )

    setLoading(false)

    if ('error' in res) return

    validateResultsLength(res)

    dispatch({
      type: SearchActionTypes.SET_SEARCH_HISTORY,
      payload: searchTerm,
    })

    dispatch({
      type: SearchActionTypes.SET_SEARCH_RESULTS,
      payload: res,
    })
  }

  const seeResults = () => {
    dispatch({
      type: SearchActionTypes.SET_RESULTS,
      payload: searchResults,
    })
    setSearchBoxActive(false)
  }

  const setSearchBoxActive = (isActive: boolean) => {
    dispatch({
      type: SearchActionTypes.SET_SEARCH_BOX_ACTIVE,
      payload: isActive,
    })
  }

  const setLoading = (loader: boolean) => {
    dispatch({
      type: SearchActionTypes.SET_LOADING,
      payload: loader,
    })
  }

  const setSearchTerm = (searchText: string) => {
    ~~dispatch({
      type: SearchActionTypes.SET_SEARCH_TERM,
      payload: searchText,
    })
    if (!searchText) return
    if (timeoutSearch) clearTimeout(timeoutSearch)
    setSearchBoxActive(true)
    setLoading(true)
    const timeout = setTimeout(() => {
      search(searchText)
    }, 850)
    setTimeoutSearch(timeout)
  }

  const searchMore = async () => {
    setLoading(true)

    const res = await searchService({
      searchTerm,
      skipCount: state.results.length,
      pageSize: SEARCH_RESULTS_PAGE_SIZE,
    })

    setLoading(false)

    if ('error' in res) return

    validateResultsLength(res)

    dispatch({
      type: SearchActionTypes.SET_MORE_RESULTS,
      payload: res,
    })
  }

  const validateResultsLength = (responseResults: IRecommendation[]) => {
    if (responseResults.length < SEARCH_RESULTS_PAGE_SIZE) {
      dispatch({
        type: SearchActionTypes.SET_NO_MORE_RESULTS,
        payload: true,
      })
    }
  }

  return (
    <SearchContextV2.Provider
      value={{
        ...state,
        search,
        seeResults,
        setSearchBoxActive,
        setSearchTerm,
        setLoading,
        searchMore,
        isOnSearchPage,
      }}
    >
      {children}
    </SearchContextV2.Provider>
  )
}
